import { Plus, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Button, Input, Switch } from '@aimmo/ui';

type AttributeValue = string | number | boolean | null;
type Attributes = Record<string, AttributeValue>;

export interface AttributesFieldProps {
  value: unknown;
  onChange: (value: Attributes) => void;
  disabled: boolean;
  /**
   * Keys another control owns — the property's known attributes, asked as
   * selects in the same bag. They are neither shown here nor lost: every
   * change writes them back untouched.
   */
  reserved?: readonly string[];
  /** The field's name in the form, so the reserved keys are read from the form itself. */
  name?: string;
}

function asAttributes(value: unknown): Attributes {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Attributes) : {};
}

/**
 * The long tail of a property, edited as key/value pairs (ADR-0005).
 *
 * A JSON textarea would be the cheap version of this and the wrong one: the
 * people entering "giardino: privato" are not going to balance braces, and a
 * malformed bag fails at save instead of at the keystroke.
 *
 * Types are inferred as the user types — a number stays a number, `sì`/`no`
 * becomes a switch — because the bag is queried later, and `"3"` and `3` do not
 * compare the same way.
 */
export function AttributesField({
  value,
  onChange,
  disabled,
  reserved = [],
  name,
}: AttributesFieldProps) {
  const form = useFormContext() as ReturnType<typeof useFormContext> | null;
  const attributes = asAttributes(value);
  const entries = Object.entries(attributes).filter(([key]) => !reserved.includes(key));

  const replace = (next: [string, AttributeValue][]) => {
    // Read from the form at the moment of the change, not from this render:
    // the reserved keys are written by their own fields, which do not
    // re-render this one, and a stale copy would put the old answer back.
    const current: unknown = name && form ? form.getValues(name) : value;
    const kept = Object.entries(asAttributes(current)).filter(([key]) => reserved.includes(key));
    onChange(Object.fromEntries([...kept, ...next]));
  };

  const renameKey = (index: number, key: string) => {
    replace(entries.map((entry, i) => (i === index ? [key, entry[1]] : entry)));
  };

  const setValue = (index: number, next: AttributeValue) => {
    replace(entries.map((entry, i) => (i === index ? [entry[0], next] : entry)));
  };

  return (
    <div className="grid gap-2">
      {entries.length === 0 && (
        <p className="rounded-control border border-dashed border-border px-3 py-4 text-secondary text-muted">
          Nessun attributo. Usali per ciò che non ha una colonna: infissi, pertinenze, portineria.
        </p>
      )}

      {entries.map(([key, entryValue], index) => (
        <div
          key={index}
          className="grid grid-cols-[1fr_auto] items-center gap-2 sm:grid-cols-[1fr_1fr_auto]"
        >
          <Input
            aria-label={`Attributo ${index + 1}`}
            value={key}
            disabled={disabled}
            placeholder="Chiave"
            onChange={(event) => renameKey(index, event.target.value)}
          />

          {typeof entryValue === 'boolean' ? (
            <label className="flex items-center gap-2 text-body text-ink-2">
              <Switch
                checked={entryValue}
                disabled={disabled}
                onCheckedChange={(checked) => setValue(index, checked)}
              />
              {entryValue ? 'Sì' : 'No'}
            </label>
          ) : (
            <Input
              aria-label={`Valore di ${key || index + 1}`}
              value={entryValue === null ? '' : String(entryValue)}
              disabled={disabled}
              placeholder="Valore"
              onChange={(event) => setValue(index, coerce(event.target.value))}
            />
          )}

          <button
            type="button"
            aria-label="Rimuovi attributo"
            disabled={disabled}
            className="rounded-control p-2 text-muted hover:bg-surface-sunken hover:text-danger"
            onClick={() => replace(entries.filter((_, i) => i !== index))}
          >
            <Trash2 className="size-4" />
          </button>
        </div>
      ))}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => replace([...entries, ['', '']])}
        >
          <Plus />
          Attributo
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => replace([...entries, ['', false]])}
        >
          <Plus />
          Sì / No
        </Button>
      </div>
    </div>
  );
}

/**
 * `120` is a number, `sì` is true, everything else is text. Only exact numeric
 * input converts: `20121` is a CAP and stays a number, but `3A` is a floor and
 * must not become 3.
 */
function coerce(raw: string): AttributeValue {
  const trimmed = raw.trim();
  if (trimmed === '') return '';
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);
  if (/^(sì|si|true)$/i.test(trimmed)) return true;
  if (/^(no|false)$/i.test(trimmed)) return false;
  return raw;
}
